import { api } from "./client";
import type { FileDTO } from "@titancloud/shared";

export interface ListFilesParams {
  folderId?: string | null;
  sort?: "name" | "size" | "date";
  order?: "asc" | "desc";
  limit?: number;
  cursor?: string | null;
}

/** Returns id+name for every non-deleted file in a folder — used for duplicate detection */
export async function listFileNames(
  folderId: string | null
): Promise<Array<{ id: string; name: string }>> {
  const qs = new URLSearchParams();
  if (folderId) qs.set("folderId", folderId);
  const data = await api.get<{ items: Array<{ id: string; name: string }> }>(
    `/files/names?${qs.toString()}`
  );
  return data.items;
}

export async function listFiles(params: ListFilesParams = {}): Promise<{
  files: FileDTO[];
  total: number;
  nextCursor: string | null;
}> {
  const qs = new URLSearchParams();
  if (params.folderId) qs.set("folderId", params.folderId);
  if (params.sort) qs.set("sort", params.sort);
  if (params.order) qs.set("order", params.order);
  if (params.limit) qs.set("limit", String(params.limit));
  if (params.cursor) qs.set("cursor", params.cursor);
  return api.get(`/files?${qs.toString()}`);
}

export async function getDownloadUrl(fileId: string, signal?: AbortSignal): Promise<{ url: string }> {
  return api.get(`/files/${fileId}/download-url`, signal ? { signal } : {});
}

/** Batch-fetches download URLs for several files in one request — used to prefetch preview neighbors. */
export async function getDownloadUrls(
  fileIds: string[],
  signal?: AbortSignal
): Promise<{ urls: Record<string, string> }> {
  return api.post("/files/download-urls", { fileIds }, signal ? { signal } : {});
}

/** Fetch presigned URL then immediately trigger a browser download */
export async function downloadFile(fileId: string, fileName: string): Promise<void> {
  const { url } = await getDownloadUrl(fileId);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
}

export async function getThumbnailUrl(fileId: string): Promise<{ url: string | null }> {
  return api.get(`/files/${fileId}/thumbnail-url`);
}

export async function renameFile(fileId: string, name: string): Promise<FileDTO> {
  return api.patch(`/files/${fileId}`, { name });
}

export async function softDeleteFile(fileId: string): Promise<void> {
  await api.delete(`/files/${fileId}`);
}

export async function hardDeleteFile(fileId: string): Promise<void> {
  await api.delete(`/files/${fileId}/permanent`);
}
